import { useEffect, useRef } from "react";
import { PdfDownloadButton } from "../components/navigation/PdfDownloadButton";
import { ScrollController } from "../components/navigation/ScrollController";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { About } from "../sections/About/About";
import { Contact } from "../sections/Contact/Contact";
import { Cover } from "../sections/Cover/Cover";
import { Experience } from "../sections/Experience/Experience";
import { Projects } from "../sections/Projects/Projects";
import { Skills } from "../sections/Skills/Skills";

const pageIds = ["cover", "about", "skills", "experience", "project-overview", "contact"] as const;

export function PortfolioPage() {
  const reduceMotion = useReducedMotion();
  const documentRef = useRef<HTMLElement>(null);

  const handleDownloadPdf = () => {
    window.print();
  };
  const swipeStartRef = useRef<{
    y: number;
    scrollY: number;
    pageIndex: number;
    pointerId?: number;
  } | null>(null);

  useEffect(() => {
    const documentElement = documentRef.current;
    if (!documentElement) return;

    const getPageIndex = () => {
      const marker = window.scrollY + window.innerHeight * 0.45;
      let index = 0;
      pageIds.forEach((id, pageIndex) => {
        const page = document.getElementById(id);
        if (page && page.offsetTop <= marker) index = pageIndex;
      });
      return index;
    };

    const finishSwipe = (endY: number, input: "touch" | "pointer") => {
      const swipeStart = swipeStartRef.current;
      swipeStartRef.current = null;
      if (!swipeStart) return;

      const distance = swipeStart.y - endY;
      if (Math.abs(distance) < 60) return;

      const direction = distance > 0 ? 1 : -1;
      const currentPageId = pageIds[swipeStart.pageIndex];
      if (!currentPageId) return;
      const currentPage = document.getElementById(currentPageId);
      if (!currentPage) return;

      const pageTop = currentPage.offsetTop;
      const pageBottom = pageTop + currentPage.offsetHeight;

      if (currentPageId === "project-overview") {
        const rowTops = [...document.querySelectorAll<HTMLElement>(".project-list-card")]
          .map((card) => Math.round(card.getBoundingClientRect().top + window.scrollY))
          .sort((first, second) => first - second)
          .filter((top, index, tops) => index === 0 || Math.abs(top - (tops[index - 1] ?? top)) > 12);
        const marker = swipeStart.scrollY + window.innerHeight * 0.5;
        let currentRowIndex = -1;
        rowTops.forEach((top, rowIndex) => {
          if (top <= marker) currentRowIndex = rowIndex;
        });

        const targetRowIndex = currentRowIndex + direction;
        const targetRowTop = rowTops[targetRowIndex];
        if (targetRowTop !== undefined) {
          window.scrollTo({
            top: targetRowTop - 18,
            behavior: reduceMotion ? "auto" : "smooth",
          });
          return;
        }

        if (direction < 0 && swipeStart.scrollY > pageTop + 32) {
          currentPage.scrollIntoView({
            behavior: reduceMotion ? "auto" : "smooth",
            block: "start",
          });
          return;
        }
      }

      const atBoundary = direction > 0
        ? window.scrollY + window.innerHeight >= pageBottom - 32
        : window.scrollY <= pageTop + 32;

      if (!atBoundary) {
        if (input === "pointer") {
          window.scrollBy({
            top: direction * Math.min(Math.abs(distance), window.innerHeight * 0.65),
            behavior: reduceMotion ? "auto" : "smooth",
          });
        }
        return;
      }

      const targetIndex = Math.min(
        Math.max(swipeStart.pageIndex + direction, 0),
        pageIds.length - 1,
      );
      if (targetIndex === swipeStart.pageIndex) return;
      const targetPageId = pageIds[targetIndex];
      if (!targetPageId) return;
      document.getElementById(targetPageId)?.scrollIntoView({
        behavior: reduceMotion ? "auto" : "smooth",
        block: "start",
      });
    };

    const handleTouchStart = (event: TouchEvent) => {
      if (event.target instanceof Element && event.target.closest("button, a, [role='dialog']")) {
        swipeStartRef.current = null;
        return;
      }
      const y = event.touches[0]?.clientY;
      if (y === undefined) return;
      swipeStartRef.current = {
        y,
        scrollY: window.scrollY,
        pageIndex: getPageIndex(),
      };
    };

    const handleTouchEnd = (event: TouchEvent) => {
      const y = event.changedTouches[0]?.clientY;
      if (y !== undefined) finishSwipe(y, "touch");
    };

    const handlePointerDown = (event: PointerEvent) => {
      if (event.pointerType === "touch" || event.button !== 0) return;
      if (event.target instanceof Element && event.target.closest("button, a, [role='dialog']")) return;
      swipeStartRef.current = {
        y: event.clientY,
        scrollY: window.scrollY,
        pageIndex: getPageIndex(),
        pointerId: event.pointerId,
      };
      documentElement.setPointerCapture(event.pointerId);
    };

    const handlePointerUp = (event: PointerEvent) => {
      if (swipeStartRef.current?.pointerId !== event.pointerId) return;
      if (documentElement.hasPointerCapture(event.pointerId)) {
        documentElement.releasePointerCapture(event.pointerId);
      }
      finishSwipe(event.clientY, "pointer");
    };

    const cancelPointer = () => {
      swipeStartRef.current = null;
    };

    documentElement.addEventListener("touchstart", handleTouchStart, { passive: true });
    documentElement.addEventListener("touchend", handleTouchEnd);
    documentElement.addEventListener("touchcancel", cancelPointer);
    documentElement.addEventListener("pointerdown", handlePointerDown);
    documentElement.addEventListener("pointerup", handlePointerUp);
    documentElement.addEventListener("pointercancel", cancelPointer);
    return () => {
      documentElement.removeEventListener("touchstart", handleTouchStart);
      documentElement.removeEventListener("touchend", handleTouchEnd);
      documentElement.removeEventListener("touchcancel", cancelPointer);
      documentElement.removeEventListener("pointerdown", handlePointerDown);
      documentElement.removeEventListener("pointerup", handlePointerUp);
      documentElement.removeEventListener("pointercancel", cancelPointer);
    };
  }, [reduceMotion]);

  return (
    <>
      <ScrollController />
      <PdfDownloadButton onClick={handleDownloadPdf} />
      <main ref={documentRef} className="portfolio-document">
        <Cover />
        <About />
        <Skills />
        <Experience />
        <Projects />
        <Contact />
      </main>
    </>
  );
}
